import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import LaunchIcon from '@mui/icons-material/Launch';
import PaymentsIcon from '@mui/icons-material/Payments';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import FinancialSummaryCard from '../../components/dashboard/FinancialSummaryCard';
import ApiService from '../../services/api.service';
import WalletService from '../../services/wallet.service';
import { useGroupCenters } from './useGroupCenters';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from 'recharts';

const emptySummary = {
  centers: { total: 0, active: 0 },
  workOrders: { total: 0, open: 0, inProgress: 0, completed: 0, invoiced: 0 },
  financial: { totalCollections: 0, totalPayments: 0, netFlow: 0, outstanding: 0 },
  centerPerformance: []
};

const pick = (obj, camelKey, snakeKey, fallback = 0) => {
  if (!obj) return fallback;
  const value = obj[camelKey] !== undefined ? obj[camelKey] : obj[snakeKey];
  return value !== undefined && value !== null ? value : fallback;
};

const normalizeSummary = (data = {}) => {
  const centers = data.centers || {};
  const workOrders = data.workOrders || data.work_orders || {};
  const financial = data.financial || {};
  const centerPerformance = data.centerPerformance || data.center_performance || [];

  return {
    centers: {
      total: Number(pick(centers, 'total', 'total')),
      active: Number(pick(centers, 'active', 'active'))
    },
    workOrders: {
      total: Number(pick(workOrders, 'total', 'total')),
      open: Number(pick(workOrders, 'open', 'open')),
      inProgress: Number(pick(workOrders, 'inProgress', 'in_progress')),
      completed: Number(pick(workOrders, 'completed', 'completed')),
      invoiced: Number(pick(workOrders, 'invoiced', 'invoiced'))
    },
    financial: {
      totalCollections: Number(pick(financial, 'totalCollections', 'total_collections')),
      totalPayments: Number(pick(financial, 'totalPayments', 'total_payments')),
      netFlow: Number(pick(financial, 'netFlow', 'net_flow')),
      outstanding: Number(pick(financial, 'outstanding', 'outstanding'))
    },
    centerPerformance: centerPerformance.map((row) => ({
      typingCenterId: pick(row, 'typingCenterId', 'typing_center_id', ''),
      name: pick(row, 'name', 'name', 'Typing Center'),
      code: pick(row, 'code', 'code', ''),
      totalWork: Number(pick(row, 'totalWork', 'total_work')),
      openWork: Number(pick(row, 'openWork', 'open_work')),
      inProgressWork: Number(pick(row, 'inProgressWork', 'in_progress_work')),
      completedWork: Number(pick(row, 'completedWork', 'completed_work')),
      invoicedWork: Number(pick(row, 'invoicedWork', 'invoiced_work')),
      invoiceCount: Number(pick(row, 'invoiceCount', 'invoice_count')),
      totalCollections: Number(pick(row, 'totalCollections', 'total_collections')),
      totalPayments: Number(pick(row, 'totalPayments', 'total_payments')),
      outstanding: Number(pick(row, 'outstanding', 'outstanding'))
    }))
  };
};

const formatCurrency = (value) => WalletService.formatCurrency(Number(value) || 0);

const SummaryCard = ({ title, value, icon, color, bgcolor, subtitle, tooltip }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const adaptedBgColor = isDark && bgcolor && bgcolor.startsWith('#') ? `${color}18` : bgcolor;

  return (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 600, letterSpacing: '0.02em' }}>
                {title}
              </Typography>
              {tooltip && (
                <Tooltip title={tooltip} arrow>
                  <InfoIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color, fontSize: '2rem', mb: 0.5, lineHeight: 1, overflowWrap: 'anywhere' }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500, mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              bgcolor: adaptedBgColor,
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              flexShrink: 0
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const SectionTitle = ({ title, color = 'secondary.main' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, color, letterSpacing: '0.05em' }}>
      {title}
    </Typography>
    <Divider sx={{ flexGrow: 1, borderColor: color, borderWidth: 1 }} />
  </Box>
);

const CHART_COLORS = ['#5794F2', '#73BF69', '#FF9830', '#B877D9', '#06B6D4', '#F2495C'];

const EmptyChartState = ({ message }) => (
  <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
    <Typography variant="body2">{message}</Typography>
  </Box>
);

const ChartCard = ({ title, children }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

const InvoiceTooltip = ({ active, payload, total }) => {
  if (!active || !payload || payload.length === 0) return null;
  const item = payload[0].payload;
  const percent = total > 0 ? Math.round((item.value / total) * 100) : 0;

  return (
    <Paper sx={{ p: 1, border: 1, borderColor: 'divider' }}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>{item.name}</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{item.value} invoices · {percent}%</Typography>
    </Paper>
  );
};

const WorkOrdersTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <Paper sx={{ p: 1, border: 1, borderColor: 'divider' }}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>{label}</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{payload[0].value} work orders</Typography>
    </Paper>
  );
};

const MetricStrip = ({ items }) => (
  <Card>
    <CardContent>
      <Grid container spacing={2}>
        {items.map((item) => (
          <Grid item xs={6} md key={item.label}>
            <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover', minHeight: 84 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {item.label}
              </Typography>
              <Typography sx={{ color: item.color || 'text.primary', fontSize: '1.35rem', fontWeight: 800, mt: 0.5, overflowWrap: 'anywhere' }}>
                {item.value}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </CardContent>
  </Card>
);

const GroupDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { opening, openCenter } = useGroupCenters({ loadCenters: false });
  const [summary, setSummary] = useState(emptySummary);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const loadSummary = async () => {
      try {
        setError('');
        const res = await ApiService.getGroupDashboardSummary();
        if (active) setSummary(normalizeSummary(res.data?.data || {}));
      } catch (err) {
        if (active) {
          setError(err.response?.data?.message || err.response?.data?.error || 'Failed to load group dashboard.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadSummary();

    return () => {
      active = false;
    };
  }, []);

  const { centers, workOrders, financial, centerPerformance } = summary;
  const invoiceDistributionData = centerPerformance
    .map((row, index) => ({
      name: row.name,
      value: row.invoiceCount,
      color: CHART_COLORS[index % CHART_COLORS.length]
    }))
    .filter((row) => row.value > 0);
  const totalInvoices = invoiceDistributionData.reduce((total, row) => total + row.value, 0);
  const workOrdersByCenterData = centerPerformance.map((row) => ({
    name: row.name,
    totalWork: row.totalWork
  }));
  const hasWorkOrdersByCenter = workOrdersByCenterData.some((row) => row.totalWork > 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.main', mb: 0.5 }}>
            Dashboard Overview
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : centers.total === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          <BusinessIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            No accessible typing centers
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Active centers assigned to your group will appear here.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard title="Centers" value={centers.total} icon={<BusinessIcon sx={{ color: '#06B6D4', fontSize: 28 }} />} color="#06B6D4" bgcolor="#cffafe" subtitle={`${centers.active} active`} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard title="Total Work Orders" value={workOrders.total} icon={<PendingActionsIcon sx={{ color: '#FF9830', fontSize: 28 }} />} color="#FF9830" bgcolor="#fff3e0" subtitle="Excludes cancelled" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard title="Total Collections" value={formatCurrency(financial.totalCollections)} icon={<PaymentsIcon sx={{ color: '#73BF69', fontSize: 28 }} />} color="#73BF69" bgcolor="#e8f5e9" subtitle="Active receipts" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard title="Outstanding" value={formatCurrency(financial.outstanding)} icon={<AccountBalanceWalletIcon sx={{ color: '#5794F2', fontSize: 28 }} />} color="#5794F2" bgcolor="#e3f2fd" subtitle="Final invoices unpaid" />
          </Grid>

          <Grid item xs={12}>
            <SectionTitle title="WORK ORDER OVERVIEW" color="secondary.main" />
            <MetricStrip
              items={[
                { label: 'Total', value: workOrders.total, color: 'primary.main' },
                { label: 'Open', value: workOrders.open, color: '#06B6D4' },
                { label: 'In Progress', value: workOrders.inProgress, color: 'warning.main' },
                { label: 'Completed', value: workOrders.completed, color: 'success.main' },
                { label: 'Invoiced', value: workOrders.invoiced, color: 'secondary.main' }
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <SectionTitle title="FINANCIAL OVERVIEW" color="#FF9830" />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FinancialSummaryCard
              variant="platform"
              title="Collections"
              accentColor="#73BF69"
              rows={[
                { label: 'Total Collected', value: formatCurrency(financial.totalCollections), color: '#86efac', bold: true }
              ]}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FinancialSummaryCard
              variant="platform"
              title="Payments to Thaklis"
              accentColor="#F2495C"
              rows={[
                { label: 'Total Paid', value: formatCurrency(financial.totalPayments), color: financial.totalPayments > 0 ? '#fca5a5' : '#86efac', bold: true }
              ]}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FinancialSummaryCard
              variant="platform"
              title="Cash Flow"
              accentColor="#5794F2"
              rows={[
                { label: 'Collections', value: formatCurrency(financial.totalCollections), color: '#86efac' },
                { label: 'Payments', value: formatCurrency(financial.totalPayments), color: financial.totalPayments > 0 ? '#fca5a5' : '#86efac' }
              ]}
              footer={{
                label: 'Net Flow',
                value: formatCurrency(financial.netFlow),
                color: financial.netFlow >= 0 ? '#86efac' : '#fca5a5'
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FinancialSummaryCard
              variant="platform"
              title="Receivables"
              accentColor="#FF9830"
              rows={[
                { label: 'Outstanding', value: formatCurrency(financial.outstanding), color: financial.outstanding > 0 ? '#FF9830' : '#86efac', bold: true }
              ]}
            />
          </Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <SectionTitle title="CENTER PERFORMANCE" color="#5794F2" />
            <Card>
              <CardContent sx={{ p: 0 }}>
                {isMobile ? (
                  <Box sx={{ p: 2, display: 'grid', gap: 1.5 }}>
                    {centerPerformance.map((row) => (
                      <Paper key={row.typingCenterId} variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                          <Avatar variant="rounded" sx={{ bgcolor: 'primary.light' }}>
                            <BusinessIcon />
                          </Avatar>
                          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Typography sx={{ fontWeight: 700 }} noWrap>{row.name}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{row.code || 'No code'}</Typography>
                          </Box>
                          <Button size="small" variant="contained" startIcon={<LaunchIcon />} disabled={opening === row.typingCenterId} onClick={() => openCenter({ id: row.typingCenterId })}>
                            {opening === row.typingCenterId ? 'Opening...' : 'Open'}
                          </Button>
                        </Box>
                        <Grid container spacing={1}>
                          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Open Work</Typography><Typography fontWeight={700}>{row.openWork}</Typography></Grid>
                          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Completed</Typography><Typography fontWeight={700}>{row.completedWork}</Typography></Grid>
                          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Invoiced</Typography><Typography fontWeight={700}>{row.invoicedWork}</Typography></Grid>
                          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Collections</Typography><Typography fontWeight={700}>{formatCurrency(row.totalCollections)}</Typography></Grid>
                          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Payments</Typography><Typography fontWeight={700}>{formatCurrency(row.totalPayments)}</Typography></Grid>
                          <Grid item xs={12}><Typography variant="caption" color="text.secondary">Outstanding</Typography><Typography fontWeight={700}>{formatCurrency(row.outstanding)}</Typography></Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: 'action.hover' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Center</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Open Work</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Completed</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Invoiced</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Total Collections</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Total Payments</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Outstanding</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {centerPerformance.map((row) => (
                          <TableRow key={row.typingCenterId} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar variant="rounded" sx={{ bgcolor: 'primary.light', width: 32, height: 32 }}>
                                  <BusinessIcon fontSize="small" />
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{row.name}</Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{row.code || 'No code'}</Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="right">{row.openWork}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <CheckCircleIcon sx={{ color: '#73BF69', fontSize: 16 }} />
                                {row.completedWork}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{row.invoicedWork}</TableCell>
                            <TableCell align="right">{formatCurrency(row.totalCollections)}</TableCell>
                            <TableCell align="right">{formatCurrency(row.totalPayments)}</TableCell>
                            <TableCell align="right">{formatCurrency(row.outstanding)}</TableCell>
                            <TableCell align="right">
                              <Button size="small" variant="outlined" startIcon={<LaunchIcon />} disabled={opening === row.typingCenterId} onClick={() => openCenter({ id: row.typingCenterId })} sx={{ textTransform: 'none' }}>
                                {opening === row.typingCenterId ? 'Opening...' : 'Open'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <SectionTitle title="GROUP ANALYTICS" color="#B877D9" />
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartCard title="Invoice Distribution">
              {totalInvoices > 0 ? (
                <>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={invoiceDistributionData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={58}
                          outerRadius={92}
                          paddingAngle={2}
                        >
                          {invoiceDistributionData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip content={<InvoiceTooltip total={totalInvoices} />} />
                        <text x="50%" y="47%" textAnchor="middle" dominantBaseline="middle" fill={theme.palette.text.primary} fontSize="26" fontWeight="700">
                          {totalInvoices}
                        </text>
                        <text x="50%" y="56%" textAnchor="middle" dominantBaseline="middle" fill={theme.palette.text.secondary} fontSize="12" fontWeight="600">
                          Invoices
                        </text>
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box sx={{ display: 'grid', gap: 1, mt: 1 }}>
                    {invoiceDistributionData.map((row) => (
                      <Box key={row.name} sx={{ display: 'grid', gridTemplateColumns: '12px minmax(0, 1fr) auto', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: row.color }} />
                        <Typography variant="body2" sx={{ minWidth: 0 }} noWrap>{row.name}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                <EmptyChartState message="No invoices found for this group yet." />
              )}
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartCard title="Work Orders by Center">
              {hasWorkOrdersByCenter ? (
                <Box sx={{ height: 340 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={workOrdersByCenterData}
                      layout="vertical"
                      margin={{ top: 8, right: 32, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={theme.palette.divider} />
                      <XAxis type="number" allowDecimals={false} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} axisLine={false} tickLine={false} />
                      <YAxis dataKey="name" type="category" width={120} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} axisLine={false} tickLine={false} />
                      <RechartsTooltip content={<WorkOrdersTooltip />} />
                      <Bar dataKey="totalWork" fill="#5794F2" radius={[0, 6, 6, 0]} barSize={22}>
                        <LabelList dataKey="totalWork" position="right" fill={theme.palette.text.primary} fontSize={12} fontWeight={700} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <EmptyChartState message="No work orders found for this group yet." />
              )}
            </ChartCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default GroupDashboard;
